// Media endpoints. initialLogo reserves a presigned upload URL for a company
// logo during onboarding (loginToken — before a company is selected). The raw
// file is then PUT directly to `uploadUrl` (see utils/uploadFile), bypassing the
// shared axios client; the returned `mediaId` is passed on to create-company.
// formMedia is the same reserve-then-PUT flow scoped to a form (instructions
// reference file, builder image-field reference image); getFormMedia is the read
// side, turning a stored mediaId back into a displayable URL at render time.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';

/** The generic media kind the media service speaks — 'file' for PDFs. */
export type MediaType = 'image' | 'file';

export interface InitialLogoInput {
  mediaType: 'image';
  imageName: string;
  customFolderId: string;
}

export interface InitialLogoResult {
  uploadUrl: string;
  mediaId: string;
}

export interface FormMediaInput {
  mediaType: MediaType;
  imageName: string;
  formId: string;
  /** The design version the media belongs to (builder field images). */
  versionId?: string;
}

export interface FormMediaResult {
  uploadUrl: string;
  mediaId: string;
}

export interface FormMediaUrlResult {
  url: string;
}

/** Reserve for the company's brand logo. Company-scoped (authToken), and
 *  unlike InitialLogoInput it needs no customFolderId. */
export interface BrandDetailsMediaInput {
  mediaType: 'image';
  imageName: string;
}

export interface BrandDetailsMediaResult {
  uploadUrl: string;
  mediaId: string;
}

/** Reserve for the mobile welcome screen's image. Same shape as the brand
 *  reserve, separate route so the two images stay distinct server-side. */
export interface WelcomeScreenMediaInput {
  mediaType: 'image';
  imageName: string;
}

export interface WelcomeScreenMediaResult {
  uploadUrl: string;
  mediaId: string;
}

/** The resolver's response — same shape as FormMediaUrlResult, kept separate
 *  because the two routes are scoped differently and may diverge. */
export interface CompanyProfileMediaResult {
  url: string;
}

export interface UploadPublicFileInput {
  /** Including the extension — the bucket key is derived from it. */
  fileName: string;
}

/** Note there is NO mediaId here, unlike the two reserves above: a public file is
 *  addressed by its own URL, which is why the data source import's commit payload
 *  carries a `filePathUrl` rather than an id. */
export interface UploadPublicFileResult {
  uploadUrl: string;
}

/** One attendance punch photo to resolve — the media's own kind plus its
 *  bucket file name (id + extension), exactly as the list response carries
 *  them inside clockInMedia[].media. */
export interface ClockMediaDownloadItem {
  mediaType: MediaType;
  imageName: string;
}

export const mediaApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    initialLogo: build.mutation<InitialLogoResult, InitialLogoInput>({
      query: (body) => ({ ...ROUTES.media.initialLogo, data: body }),
    }),
    // Same reserve-then-PUT flow as initialLogo, scoped to a form (authToken).
    formMedia: build.mutation<FormMediaResult, FormMediaInput>({
      query: (body) => ({ ...ROUTES.media.formMedia, data: body }),
    }),
    // Resolves a stored mediaId to a displayable URL, at render time. No cache
    // tag: a mediaId's content is immutable, so RTK Query's per-arg cache is
    // exactly the dedupe wanted — every consumer of the same id (the canvas
    // card and the properties panel, say) shares one request.
    getFormMedia: build.query<FormMediaUrlResult, string>({
      query: (id) => ({ ...ROUTES.media.getFormMedia(id) }),
    }),
    // Reserve-then-PUT again, but for a file owned by nothing — the caller keeps
    // the URL. No cache tag: this reserves, it never reads.
    uploadPublicFile: build.mutation<UploadPublicFileResult, UploadPublicFileInput>({
      query: (body) => ({ ...ROUTES.media.uploadPublicFile, data: body }),
    }),
    // Reserve-then-PUT for the company brand logo (authToken). The in-app
    // counterpart to initialLogo, which only works pre-company.
    brandDetailsMedia: build.mutation<BrandDetailsMediaResult, BrandDetailsMediaInput>({
      query: (body) => ({ ...ROUTES.media.brandDetailsMedia, data: body }),
    }),
    // Reserve-then-PUT for the mobile welcome screen image (authToken).
    welcomeScreenMedia: build.mutation<WelcomeScreenMediaResult, WelcomeScreenMediaInput>({
      query: (body) => ({ ...ROUTES.media.welcomeScreenMedia, data: body }),
    }),
    // Resolves a company-profile mediaId to a displayable URL. No cache tag,
    // for the same reason as getFormMedia: a mediaId's content is immutable,
    // so the per-arg cache is exactly the dedupe wanted.
    getCompanyProfileMedia: build.query<CompanyProfileMediaResult, string>({
      query: (id) => ({ ...ROUTES.media.companyProfileMedia(id) }),
    }),
    // Batch-resolves attendance punch photos to download URLs, keyed back by
    // imageName — one call per table page. The body is the BARE items array.
    // No cache tag: media content is immutable, so the per-arg cache is
    // exactly the dedupe wanted (same reasoning as getFormMedia).
    downloadClockInMedia: build.query<Record<string, string>, ClockMediaDownloadItem[]>({
      query: (items) => ({ ...ROUTES.media.downloadClockInMedia, data: items }),
      transformResponse: (res: { items: { imageName: string; downloadUrl: string }[] }) =>
        Object.fromEntries(res.items.map((m) => [m.imageName, m.downloadUrl])),
    }),
  }),
});

export const {
  useInitialLogoMutation,
  useFormMediaMutation,
  useGetFormMediaQuery,
  useUploadPublicFileMutation,
  useDownloadClockInMediaQuery,
  useBrandDetailsMediaMutation,
  useWelcomeScreenMediaMutation,
  useGetCompanyProfileMediaQuery,
} = mediaApi;
